'use client'

import * as React from 'react'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import { cn } from '@/lib/utils'

const TooltipProvider = TooltipPrimitive.Provider

const Tooltip = TooltipPrimitive.Root

const TooltipTrigger = TooltipPrimitive.Trigger

/**
 * The content, **portalled to the end of `<body>`**.
 *
 * Without the portal Radix mounts the tooltip as a DOM sibling of its trigger,
 * where every ancestor's `overflow` applies to it. Three places in this app clip
 * a tooltip that way, and `z-index` cannot help with any of them — a clipped
 * element is clipped however high it stacks:
 *
 * - the case-file header (`app/cases/[id]/page.tsx`) is `overflow-hidden`,
 *   which it needs so the ampersand watermark stays inside the card. The 100%
 *   classification meter sits in it and its tooltip was cut at the card edge.
 * - `ClassificationCard` is the same pattern with two meters in it.
 * - `ui/table` wraps every table in `overflow-x-auto`, and CSS resolves
 *   `overflow-y: visible` beside an `auto` sibling to `auto` — so every
 *   confidence tooltip in the completeness table was clipped at the row band
 *   too, less obviously because `side='left'` kept most of it inside.
 *
 * Portalling fixes all three at once and every tooltip added later. `z-tooltip`
 * (600) still beats `modal` (400), so a tooltip triggered from inside a sheet or
 * a dialog paints above it now that both sit at body level.
 *
 * `collisionPadding` keeps a tooltip near the viewport edge from overflowing the
 * window instead: Radix flips or shifts it rather than letting it run off. It is
 * what makes `side` a preference rather than an instruction, which is the right
 * relationship — a caller asking for `left` wants left *if it fits*.
 */
const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, collisionPadding = 8, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      collisionPadding={collisionPadding}
      className={cn(
        'z-tooltip overflow-hidden',
        'rounded-lg px-3 py-1.5',
        'bg-brand-navy text-white',
        'text-xs font-medium',
        'shadow-float',
        'animate-fade-in',
        className
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
))
TooltipContent.displayName = TooltipPrimitive.Content.displayName

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
